import type { Storefront } from '@arabat/contracts';
import { cn } from '@arabat/ui';
import { useMoneyFormat } from '@arabat/web-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalName } from '../shared.js';

type Zone = Storefront['deliveryAreas'][number];
export type AreaChoice = { areaId: string | null; placeId: string | null };

/** Arabic and English letters compared loosely: alef forms, taa marbuta, alef maqsura, case. */
const fold = (text: string) =>
  text.toLowerCase().replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي').trim();

const OTHER = 'other';

/**
 * Where to deliver (ADR-0140): the governorates the store delivers to open one at a time to
 * show their areas as buttons, with a search over all of them. Zones without places (named
 * areas from before) sit in their own group. Once chosen, one line remains with "Change".
 */
export function AreaPicker({
  zones,
  regions,
  value,
  onChange,
  error,
}: {
  zones: Zone[];
  regions: Storefront['deliveryRegions'];
  value: AreaChoice;
  onChange: (choice: { areaId: string; placeId: string | null }) => void;
  error?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const name = useLocalName();
  const money = useMoneyFormat();
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const comma = i18n.language.startsWith('ar') ? '، ' : ', ';
  const collator = new Intl.Collator(i18n.language);
  const zone = zones.find((z) =>
    value.placeId ? z.places.some((p) => p.id === value.placeId) : z.id === value.areaId,
  );
  const place = zone?.places.find((p) => p.id === value.placeId) ?? null;
  const chosen = zone && (place || zone.places.length === 0) ? zone : null;
  const feeLine = (z: Zone) =>
    [money(z.fee), z.estimatedMinutes ? t('checkout.minutes', { count: z.estimatedMinutes }) : null]
      .filter(Boolean)
      .join(' · ');

  if (chosen && !editing) {
    const region = place ? regions.find((r) => r.id === place.regionId) : undefined;
    return (
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{t('checkout.area')}</span>
        <button
          type="button"
          onClick={() => {
            setEditing(true);
            setOpen(place?.regionId ?? null);
          }}
          className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-start hover:border-primary/50"
          data-testid="area-summary"
        >
          <span aria-hidden className="text-lg">
            📍
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="font-semibold">
              {place ? [place, ...(region ? [region] : [])].map(name).join(comma) : name(chosen)}
            </span>
            <span className="text-xs text-fg-muted">{feeLine(chosen)}</span>
          </span>
          <span className="text-sm font-semibold text-primary">{t('checkout.change')}</span>
        </button>
      </div>
    );
  }

  const q = fold(query);
  const matches = (x: { nameEn: string | null; nameAr: string | null }) =>
    !q || fold(`${x.nameAr ?? ''} ${x.nameEn ?? ''}`).includes(q);
  const groups = [
    ...regions.map((r) => ({
      id: r.id,
      label: name(r),
      items: zones
        .flatMap((z) => z.places.filter((p) => p.regionId === r.id).map((p) => ({ z, p })))
        .filter(({ p }) => matches(p))
        .sort((a, b) => collator.compare(name(a.p), name(b.p)))
        .map(({ z, p }) => ({
          key: p.id,
          label: name(p),
          zone: z,
          placeId: p.id as string | null,
        })),
    })),
    {
      id: OTHER,
      label: t('checkout.otherAreas'),
      items: zones
        .filter((z) => z.places.length === 0 && matches(z))
        .map((z) => ({ key: z.id, label: name(z), zone: z, placeId: null as string | null })),
    },
  ].filter((g) => g.items.length > 0);
  // Without governorates there is nothing to open: the areas show at once.
  const flat = regions.length === 0;
  const pick = (zoneId: string, placeId: string | null) => {
    onChange({ areaId: zoneId, placeId });
    setEditing(false);
    setQuery('');
  };
  return (
    <fieldset className="flex min-w-0 flex-col gap-2" data-testid="area-picker">
      <legend className="mb-1 text-sm font-medium">{t('checkout.area')}</legend>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('checkout.searchArea')}
        aria-label={t('checkout.searchArea')}
        className="h-11 rounded-xl border border-border bg-surface px-3 text-base outline-none focus:border-primary"
      />
      {groups.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('checkout.noAreaMatch')}</p>
      ) : null}
      <div className="flex flex-col gap-2">
        {groups.map((g) => {
          const expanded = flat || Boolean(q) || open === g.id;
          return (
            <div key={g.id} className="overflow-hidden rounded-xl border border-border bg-surface">
              {flat ? null : (
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpen(open === g.id ? null : g.id)}
                  className="flex w-full items-center justify-between gap-2 bg-surface-muted/70 px-4 py-3 text-start font-semibold text-primary"
                >
                  <span>{g.label}</span>
                  <span
                    aria-hidden
                    className={cn('text-fg transition-transform', expanded && 'rotate-180')}
                  >
                    ▾
                  </span>
                </button>
              )}
              {expanded ? (
                <div className="grid grid-cols-2 gap-2 p-2" role="group" aria-label={g.label}>
                  {g.items.map((item) => {
                    const selected =
                      item.zone.id === value.areaId && item.placeId === value.placeId;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => pick(item.zone.id, item.placeId)}
                        className={cn(
                          'flex min-w-0 flex-col items-start rounded-lg border px-3 py-2 text-start text-sm transition-colors',
                          selected
                            ? 'border-primary bg-primary text-primary-fg'
                            : 'border-border bg-surface hover:border-primary/50',
                        )}
                      >
                        <span className="font-medium break-words">{item.label}</span>
                        <span className={cn('text-xs', selected ? 'opacity-90' : 'text-fg-muted')}>
                          {money(item.zone.fee)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </fieldset>
  );
}
